import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useMemo } from 'react';
import { getCourseBySlug, type Lesson } from '@/lib/learning/courses';
import { GROWTH_101_LESSONS } from '@/lib/learning/growth-101';
import { CONTENT_MARKETING_LESSONS } from '@/lib/learning/content-marketing';
import { ROADMAP_PLANNING_LESSONS } from '@/lib/learning/roadmap-planning';
import { SEO_REFERENCEMENT_LESSONS } from '@/lib/learning/seo-referencement';
import { SALES_INTELLIGENCE_LESSONS } from '@/lib/learning/sales-intelligence';
import { MARKETING_AUTOMATION_LESSONS } from '@/lib/learning/marketing-automation';
import { REFERRAL_VIRALITY_LESSONS } from '@/lib/learning/referral-virality';
import { CUSTOMER_SUCCESS_LESSONS } from '@/lib/learning/customer-success';
import { DATA_ANALYTICS_LESSONS } from '@/lib/learning/data-analytics';
import { STRIPE_BILLING_V2_LESSONS } from '@/lib/learning/stripe-billing-v2';
import { AI_ROUTER_CONFIG_LESSONS } from '@/lib/learning/ai-router-config';
import { PLAN_GATES_MANAGEMENT_LESSONS } from '@/lib/learning/plan-gates-management';
import { useLearningProgress } from '@/hooks/useLearningProgress';
import { LessonListItem } from './components/LessonListItem';
import { LearningProgressBar } from './components/LearningProgressBar';

const LESSONS_MAP: Record<string, Lesson[]> = {
  'growth-101': GROWTH_101_LESSONS,
  'content-marketing': CONTENT_MARKETING_LESSONS,
  'roadmap-planning': ROADMAP_PLANNING_LESSONS,
  'seo-referencement': SEO_REFERENCEMENT_LESSONS,
  'sales-intelligence': SALES_INTELLIGENCE_LESSONS,
  'marketing-automation': MARKETING_AUTOMATION_LESSONS,
  'referral-virality': REFERRAL_VIRALITY_LESSONS,
  'customer-success': CUSTOMER_SUCCESS_LESSONS,
  'data-analytics': DATA_ANALYTICS_LESSONS,
  'stripe-billing-v2': STRIPE_BILLING_V2_LESSONS,
  'ai-router-config': AI_ROUTER_CONFIG_LESSONS,
  'plan-gates-management': PLAN_GATES_MANAGEMENT_LESSONS,
};

/**
 * Page d'un parcours — liste des lecons avec progression.
 */
export default function CoursePage() {
  const { courseSlug = '' } = useParams();
  const navigate = useNavigate();
  const course = getCourseBySlug(courseSlug);
  const lessons = LESSONS_MAP[courseSlug] ?? [];
  const { isLessonCompleted, completedCountForCourse } = useLearningProgress();

  const completedCount = useMemo(() => completedCountForCourse(courseSlug), [courseSlug, completedCountForCourse]);

  if (!course) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-12 text-center">
        <p className="text-slate-500 dark:text-slate-400">Parcours introuvable.</p>
        <button
          onClick={() => navigate('/learning')}
          className="mt-4 text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
        >
          Retour au centre d'apprentissage
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-6 py-8">
      {/* Back */}
      <button
        onClick={() => navigate('/learning')}
        className="mb-6 flex items-center gap-1.5 text-sm font-medium text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Centre d'apprentissage
      </button>

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl font-bold text-slate-900 dark:text-white">{course.title}</h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{course.subtitle}</p>
        <div className="mt-3 max-w-xs">
          <LearningProgressBar completed={completedCount} total={course.lessonCount} />
        </div>
      </div>

      {/* Lessons list */}
      <div className="space-y-2">
        {lessons.map((lesson, idx) => (
          <LessonListItem
            key={lesson.slug}
            lesson={lesson}
            courseSlug={courseSlug}
            index={idx}
            isCompleted={isLessonCompleted(courseSlug, lesson.slug)}
          />
        ))}
      </div>

      {lessons.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-200 dark:border-slate-700 p-8 text-center">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Le contenu de ce parcours sera bientot disponible.
          </p>
        </div>
      )}
    </div>
  );
}
